import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FormControl, InputLabel, OutlinedInput, FormHelperText } from "@mui/material";
import Grid from '@mui/material/Grid2';
import { v4 as uuidv4 } from "uuid";

import DisplayString from "@components/input/strings/DisplayString";

interface Props {
  value: string | number | null;
  message?: string;
  name: string;
  onBlur?: Function | null;
  onChange?: Function | null;
  multiline?: boolean;
  type?: string;
  color?: string;
  disabled?: boolean;
  width?: number;
  className?: string;
  change_time?: number;
  log_it?: boolean;
  onSelect?: (e: React.SyntheticEvent<HTMLDivElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<Element>) => void;
}

export default function StringBox({
  value,
  message = "",
  name,
  onBlur = null,
  onChange = null,
  multiline = false,
  type = "text",
  color = "",
  disabled = false,
  width = -1,
  className = "",
  change_time = 0,
  log_it = false,
  onSelect,
  onKeyDown,
}: Props) {
  const [last_value, setLastValue] = useState<string>(value ? `${value}` : "");
  const [inputValue, setInputValue] = useState<string>(value ? `${value}` : "");
  const [do_blur, setDoBlur] = useState<boolean>(false);

  const trueId = useRef<string>(uuidv4().toString());
  const isFocused = useRef(false);

  useEffect(() => {
    if (isFocused.current) return;
    if (`${value}` !== last_value) {
      setLastValue(`${value}`);
      setInputValue(`${value}`);
    }
  }, [value, last_value]);

  // When change_time updates, it means a programmatic commit happened
  // (toolbar action, tag insertion, etc.) — force-sync regardless of focus.
  const prev_change_time = useRef(change_time);
  useEffect(() => {
    if (change_time !== 0 && change_time !== prev_change_time.current) {
      prev_change_time.current = change_time;
      const v = `${value}`;
      setLastValue(v);
      setInputValue(v);
    }
  }, [change_time, value]);

  const handleBlur = useCallback(() => {
    if (log_it) {
      debugger;
    }
    let new_value = inputValue.trim();
    while (new_value.includes("\t")) {
      new_value = new_value.replace("\t", " ");
    }
    while (new_value.includes("  ")) {
      new_value = new_value.replace("  ", " ");
    }
    if (type === "number") {
      const numberValue = +new_value;
      if (isNaN(numberValue)) {
        new_value = "0";
      } else {
        new_value = numberValue.toString();
      }
    }

    if (new_value !== inputValue) {
      setInputValue(new_value);
    }

    if (onBlur) {
      onBlur(new_value);
    } else if (onChange) {
      onChange(new_value);
    }
  }, [inputValue, onBlur, onChange, type]);

  useEffect(() => {
    if (do_blur) {
      setDoBlur(false);
      handleBlur();
    }
  }, [do_blur, handleBlur]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (log_it) {
      debugger;
    }
    const newValue = event.target.value;
    setInputValue(newValue);
    // For multiline, fire onChange on every keystroke so the parent
    // always has the current value (e.g. for toolbar actions like bold).
    if (multiline && onChange) {
      onChange(newValue);
    } else if (type === "number" && onBlur) {
      onBlur(newValue);
    }
  };

  // Single keydown handler — calls parent onKeyDown first, then handles Enter locally.
  const handleKeyDown = (event: React.KeyboardEvent<Element>) => {
    if (onKeyDown) {
      onKeyDown(event);
    }
    if (event.key === 'Enter' && !multiline && onBlur) {
      setDoBlur(true);
    }
  };

  const renderControl = () => {
    if (disabled) {
      if (multiline) {
        return (
          <Grid container spacing={0} direction="row">
            <Grid size={12} className={"header padded"}>
              {name}
            </Grid>
            <Grid size={12}>
              <DisplayString str={inputValue} />
            </Grid>
          </Grid>
        );
      } else {
        return (
          <div>
            <span className={"header padded"}>{name}:</span>
            <span>{inputValue}</span>
          </div>
        );
      }
    } else {
      const label = <InputLabel htmlFor={`stringInput_${trueId.current}`}>{name}</InputLabel>;
      const input =
        <OutlinedInput
          id={`stringInput_${trueId.current}`}
          name={`stringInput_${trueId.current}`}
          type={type}
          autoComplete="Off"
          value={inputValue}
          disabled={disabled}
          color="primary"
          onKeyDown={handleKeyDown}
          onChange={handleChange}
          onFocus={() => { isFocused.current = true; }}
          onBlur={() => {
            isFocused.current = false;
            if (type !== "number" && onBlur) {
              setDoBlur(true);
            }
          }}
          fullWidth
          multiline={multiline}
          maxRows={15}
          sx={{ textarea: { overflowY: "auto !important" } }}
          onSelect={onSelect}
        />;
      const helper = <FormHelperText>{message}</FormHelperText>;

      if (width === -1) {
        return (
          <FormControl variant="outlined" fullWidth>
            {label}
            {input}
            {helper}
          </FormControl>
        );
      } else {
        return (
          <FormControl variant="outlined" style={{ width }}>
            {label}
            {input}
            {helper}
          </FormControl>
        );
      }
    }
  };

  if (color !== "") {
    const colorValue = color === "blue" ? "#1C9AEF" : color;
    return (
      <div style={{ border: `1px solid ${colorValue}` }} className={className}>
        {renderControl()}
      </div>
    );
  } else if (className !== "") {
    return (
      <div className={className}>
        {renderControl()}
      </div>
    );
  } else {
    return renderControl();
  }
}